import React, { useEffect } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import type { Control, UseFormGetValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Button, Input, Label } from '@shared/components';
import type { ProductFeeFormInput } from '../schemas/productFee.schema';
import { useVatDerivedCost } from '../hooks/useVatDerivedCost';

interface Props {
    control: Control<ProductFeeFormInput>;
    isPercentageType: boolean;
    vatRatePercent: string;
    setValue: UseFormSetValue<ProductFeeFormInput>;
    getValues: UseFormGetValues<ProductFeeFormInput>;
    watch: UseFormWatch<ProductFeeFormInput>;
}

interface BandRowProps {
    index: number;
    control: Control<ProductFeeFormInput>;
    isPercentageType: boolean;
    vatRatePercent: string;
    setValue: UseFormSetValue<ProductFeeFormInput>;
    watch: UseFormWatch<ProductFeeFormInput>;
    onRemove: () => void;
}

const BandRow: React.FC<BandRowProps> = ({ index: i, control, isPercentageType, vatRatePercent, setValue, watch, onRemove }) => {
    useVatDerivedCost({
        watch,
        setValue,
        netField:   `bands.${i}.cost_net`,
        grossField: `bands.${i}.cost_gross`,
        fixedRatePercent:  vatRatePercent,
        enabled:    !isPercentageType,
    });

    return (
        <div className="grid grid-cols-1 gap-2 rounded border border-mp-border p-3 md:grid-cols-4">
            <div>
                <Label htmlFor={`bands.${i}.band_index`} required>Band</Label>
                <Controller
                    name={`bands.${i}.band_index`}
                    control={control}
                    render={({ field: f, fieldState: { error: fe } }) => (
                        <Input
                            id={`bands.${i}.band_index`}
                            type="number"
                            min="1"
                            max="10"
                            step={1}
                            value={f.value ?? ''}
                            onChange={(e) => f.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                            onBlur={f.onBlur}
                            error={!!fe}
                            hint={fe?.message}
                        />
                    )}
                />
            </div>

            {isPercentageType ? (
                <div>
                    <Label htmlFor={`bands.${i}.percentage`} required>Percentage</Label>
                    <Controller
                        name={`bands.${i}.percentage`}
                        control={control}
                        render={({ field: f, fieldState: { error: fe } }) => (
                            <Input
                                id={`bands.${i}.percentage`}
                                type="number"
                                min="0"
                                max="100"
                                step={0.0001}
                                value={(f.value as string | null) ?? ''}
                                onChange={(e) => f.onChange(e.target.value === '' ? null : e.target.value)}
                                onBlur={f.onBlur}
                                error={!!fe}
                                hint={fe?.message}
                                placeholder="e.g. 5.0000"
                            />
                        )}
                    />
                </div>
            ) : (
                <>
                    <div>
                        <Label htmlFor={`bands.${i}.cost_net`} required>Cost Net (£)</Label>
                        <Controller
                            name={`bands.${i}.cost_net`}
                            control={control}
                            render={({ field: f, fieldState: { error: fe } }) => (
                                <Input
                                    id={`bands.${i}.cost_net`}
                                    type="number"
                                    min="0"
                                    step={0.0001}
                                    value={(f.value as string | null) ?? ''}
                                    onChange={(e) => f.onChange(e.target.value === '' ? null : e.target.value)}
                                    onBlur={f.onBlur}
                                    error={!!fe}
                                    hint={fe?.message}
                                    placeholder="0.00"
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label htmlFor={`bands.${i}.cost_gross`}>Cost Gross (£)</Label>
                        <Controller
                            name={`bands.${i}.cost_gross`}
                            control={control}
                            render={({ field: f, fieldState: { error: fe } }) => (
                                <Input
                                    id={`bands.${i}.cost_gross`}
                                    type="number"
                                    readOnly
                                    value={(f.value as string | null) ?? ''}
                                    error={!!fe}
                                    hint={fe?.message}
                                    placeholder="0.00"
                                />
                            )}
                        />
                    </div>
                </>
            )}

            <div className="flex items-end">
                <Button type="button" size="sm" variant="outline" onClick={onRemove}>
                    Remove
                </Button>
            </div>
        </div>
    );
};

export const BandSubEditor: React.FC<Props> = ({ control, isPercentageType, vatRatePercent, setValue, getValues, watch }) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'bands',
    });

    // A value-type switch hides one side of every band's inputs. Clear the side that is
    // now hidden, otherwise the stale values fail the dynamic schema with no editable
    // control to correct them and the submit button stays disabled.
    useEffect(() => {
        (getValues('bands') ?? []).forEach((b, i) => {
            if (isPercentageType) {
                if (b.cost_net != null) {
                    setValue(`bands.${i}.cost_net`, null);
                }
                if (b.cost_gross != null) {
                    setValue(`bands.${i}.cost_gross`, null);
                }
            } else if (b.percentage != null) {
                setValue(`bands.${i}.percentage`, null);
            }
        });
    }, [isPercentageType, getValues, setValue]);

    const handleAdd = () => {
        const nextIndex = fields.length + 1;
        if (nextIndex > 10) {
            return;
        }
        append({ band_index: nextIndex, cost_net: null, cost_gross: null, percentage: null });
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Label>Value Scale Bands</Label>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleAdd}
                    disabled={fields.length >= 10}
                >
                    Add Band
                </Button>
            </div>

            {fields.length === 0 && (
                <p className="text-sm text-mp-muted">No bands configured. The Default value will be used.</p>
            )}

            {fields.map((field, i) => (
                <BandRow
                    key={field.id}
                    index={i}
                    control={control}
                    isPercentageType={isPercentageType}
                    vatRatePercent={vatRatePercent}
                    setValue={setValue}
                    watch={watch}
                    onRemove={() => remove(i)}
                />
            ))}
        </div>
    );
};
